"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Modal, ModalHeader, ModalBody, Button, Alert, Spinner, Row, Col, Card, CardBody } from "reactstrap"
import {
  FaDownload,
  FaPrint,
  FaQrcode,
  FaUser,
  FaIdCard,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaCalendarAlt,
  FaHashtag,
  FaCopy,
} from "react-icons/fa"
import { toast } from "react-toastify"
import QRCode from "qrcode"

// Create a helper function for dynamic images (replace with your actual implementation)
const dynamicImage = (imagePath: string): string => {
  // This should match your project's image loading logic
  // For now, using a simple path construction
  return `/assets/images/${imagePath}`
}

interface BadgeInfo {
  person_full_name: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  exhibition_description: string
  exhibition_venue?: string // Added venue field
  start_date: string
  end_date: string
  exhibition_phone?: string // Added exhibition contact phone
  exhibition_email?: string // Added exhibition contact email
  stand_category: string // Changed from category to stand_category
  print_type: string
  sequence_number: number
  verification_code: string
  generated_at: string
  id_doc_number?: string
  email?: string
  phone?: string
  role_color?: string // Added for database color
}

interface VerificationPayload {
  verification_code: string
}

interface BadgeData {
  badge_info: BadgeInfo
  verification_payload: VerificationPayload
  photo_url: string | null
  has_photo: boolean
}

interface TemplateConfig {
  width: number
  height: number
  colors: {
    primary: string
    secondary: string
    background: string
    text: string
  }
  fonts: {
    title: { size: number; weight: string }
    name: { size: number; weight: string }
    role: { size: number; weight: string }
    info: { size: number; weight: string }
    small: { size: number; weight: string }
  }
  layout: {
    photo_size: number
    photo_position: { x: number; y: number }
    qr_size: number
    show_qr: boolean
    show_logo: boolean
  }
}

// Add isPreviewMode prop to the Props interface
interface Props {
  isOpen: boolean
  onToggle: () => void
  badgeData: BadgeData | null
  templateConfig?: TemplateConfig | null
  onComplete: (badgeImage: string) => void
  isPreviewMode?: boolean // Add this new prop
}

// Update the component signature
const EnhancedPSFBadgeDesigner = ({
  isOpen,
  onToggle,
  badgeData,
  templateConfig,
  onComplete,
  isPreviewMode = false,
}: Props) => {
  const canvasRef = useRef<HTMLImageElement>(null)
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null)
  const [badgeImage, setBadgeImage] = useState<string | null>(null)
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null) // QR code as data URL string
  const [qrImageElement, setQrImageElement] = useState<HTMLImageElement | null>(null) // QR code as Image object
  const [copied, setCopied] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  const [photoLoaded, setPhotoLoaded] = useState(false)
  const [photoImage, setPhotoImage] = useState<HTMLImageElement | null>(null)
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null)
  const [isGenerationComplete, setIsGenerationComplete] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [exhibitionText, setExhibitionText] = useState<string>("")
  const [showLogo, setShowLogo] = useState(false)

  // Badge dimensions (portrait with increased height)
  const BADGE_WIDTH = 220
  const BADGE_HEIGHT = 314 // Increased height for portrait
  const DPI_SCALE = 2

  // PSF Colors
  const PSF_ORANGE = "#ba4a00"
  const PSF_GREEN = "#0e6655"
  //"#137813ff"
  const ORGINAL_ORANGE = "#f5891f"
  const PSF_DARK_GREEN = "#088344"

  // Category colors based on stand_category
  const categoryColors = {
    partner: "#8B5CF6",
    platinum: "#E5E7EB",
    gold: "#F59E0B",
    silver: "#9CA3AF",
    staff: PSF_GREEN,
    // Default fallback
    exhibitor: PSF_ORANGE,
  }

  // Add PSF logo state
  const [psfLogoImage, setPsfLogoImage] = useState<HTMLImageElement | null>(null)

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBadgeImage(null)
      setQrCodeImage(null)
      setQrImageElement(null)
      setCopied(false)
      setPhotoLoaded(false)
      setPhotoImage(null)
      setBackgroundImage(null)
      setPsfLogoImage(null)
      setIsGenerationComplete(false)
      setExhibitionText("") // Reset exhibitionText when modal closes
    }
  }, [isOpen])

  // Load background image and PSF logo
  useEffect(() => {
    if (isOpen) {
      const loadImages = async () => {
        // Load PSF Logo
        const logoImg = new window.Image()
        logoImg.crossOrigin = "anonymous"
        const logoPromise = new Promise<HTMLImageElement | null>((resolve) => {
          logoImg.onload = () => resolve(logoImg)
          logoImg.onerror = () => {
            console.warn("PSF logo failed to load")
            resolve(null) // Resolve with null on error
          }
          logoImg.src = dynamicImage("logo.PNG")
        })

        // Load Background Image (convention.png - though not used in the crown design, keep for consistency if it was intended)
        const bgImg = new window.Image()
        bgImg.crossOrigin = "anonymous"
        const bgPromise = new Promise<HTMLImageElement | null>((resolve) => {
          bgImg.onload = () => resolve(bgImg)
          bgImg.onerror = () => {
            console.warn("Background image failed to load")
            resolve(null)
          }
          bgImg.src = "/assets/images/convention.png"
        })

        const [loadedLogo, loadedBg] = await Promise.all([logoPromise, bgPromise])
        setPsfLogoImage(loadedLogo)
        setBackgroundImage(loadedBg)
      }
      loadImages()
    }
  }, [isOpen])

  // Generate high-quality QR code (as data URL string)
  useEffect(() => {
    if (isOpen && badgeData) {
      const qrData = badgeData.badge_info.verification_code
      QRCode.toDataURL(qrData, {
        width: 100 * DPI_SCALE, // Appropriate size for portrait
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
        errorCorrectionLevel: "H",
      })
        .then(setQrCodeImage)
        .catch((error) => {
          console.error("QR Code generation failed:", error)
          toast.error("QR Code generation failed")
        })
    }
  }, [badgeData, isOpen])

  // Load QR Code image element from data URL string
  useEffect(() => {
    if (qrCodeImage) {
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.onload = () => setQrImageElement(img)
      img.onerror = () => {
        console.warn("QR image element failed to load")
        setQrImageElement(null)
      }
      img.src = qrCodeImage
    } else {
      setQrImageElement(null)
    }
  }, [qrCodeImage])

  useEffect(() => {
    if (shouldClose && isGenerationComplete) {
      onToggle()
      setShouldClose(false)
    }
  }, [shouldClose, isGenerationComplete, onToggle])

  // Load photo with higher quality
  useEffect(() => {
    if (isOpen && badgeData?.has_photo && badgeData.photo_url) {
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        setPhotoImage(img)
        setPhotoLoaded(true)
      }
      img.onerror = () => {
        setPhotoLoaded(true) // Still set true even on error to proceed
        console.error("Error loading photo")
      }
      img.src = badgeData.photo_url
    } else if (badgeData) {
      setPhotoLoaded(true) // No photo, so it's "loaded"
    }
  }, [badgeData, isOpen])

  // Helper function to draw rounded rectangle with specific corners
  const drawRoundedRectTopOnly = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height)
    ctx.lineTo(x, y + height)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  // Helper function to draw fully rounded rectangle
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  // Helper function to wrap text to multiple lines
  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    textAlign: "left" | "center" | "right" = "left",
  ) => {
    const words = text.split(" ")
    let line = ""
    const currentY = y
    const lines: string[] = []
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + " "
      const metrics = ctx.measureText(testLine)
      const testWidth = metrics.width
      if (testWidth > maxWidth && n > 0) {
        lines.push(line.trim())
        line = words[n] + " "
      } else {
        line = testLine
      }
    }
    lines.push(line.trim())
    // Draw each line
    lines.forEach((lineText, index) => {
      ctx.textAlign = textAlign
      ctx.fillText(lineText, x, currentY + index * lineHeight)
    })
    return lines.length * lineHeight // Return total height used
  }

  // Enhanced badge generation matching crown design exactly
  const generateBadge = useCallback(() => {
    if (!badgeData || !hiddenCanvasRef.current || !qrImageElement) return // Basic checks

    // Conditional checks for images
    if (badgeData.has_photo && !photoImage) return // If photo is expected but not loaded
    if (showLogo && !psfLogoImage) return // If logo is expected but not loaded

    setIsRendering(true)
    const canvas = hiddenCanvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    try {
      // Set high-resolution dimensions
      canvas.width = BADGE_WIDTH * DPI_SCALE
      canvas.height = BADGE_HEIGHT * DPI_SCALE
      // Scale context for high DPI
      ctx.scale(DPI_SCALE, DPI_SCALE)
      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = "high"
      // Clear canvas
      ctx.clearRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT)

      // Mixed green/black color for crown design
      const mixedGreenBlack = "#212f3d" // Dark green-black mix
      // 1. BACKGROUND - Cover entire badge with mixed green/black
      ctx.fillStyle = mixedGreenBlack
      ctx.fillRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT)

      // Draw the header with the logo on the left side
      const headerHeight = 44 // Set the height of the header section
      const rightSectionHeight = 30 // Reduce the height of the right section

      // 2. Header section - Split design with two parts: left (black) and right (white)
      const x = 0 // Set x position to the very left of the canvas
      const y = 0 // Start at the top of the canvas
      const w = BADGE_WIDTH // Full width of the badge
      const h = headerHeight
      const logoSize = 44 // Set the height of the logo

      // Left part (black background for the remaining space after the logo)
      ctx.fillStyle = mixedGreenBlack // Black background for the left part (after logo)
      ctx.fillRect(logoSize + 2, y, w / 2 - (logoSize + 2), h) // Fill the black space after the logo

      // Right part (white background with curved left-bottom corner)
      const xRight = w / 2 + 4 // Adjust to place it in the right half of the header
      const radius = 10 // Radius of the curve
      const rightWidth = w / 2 - 4 // Adjust the width of the right part

      // Draw the right section with reduced height and the curve at the bottom-left corner
      ctx.fillStyle = "#ffffff" // White background for the right part
      ctx.beginPath()
      // top-left
      ctx.moveTo(xRight, y)
      // top edge
      ctx.lineTo(xRight + rightWidth, y)
      // right edge
      ctx.lineTo(xRight + rightWidth, y + rightSectionHeight) // Reduced height here
      // bottom edge, stopping radius in from left
      ctx.lineTo(xRight + radius, y + rightSectionHeight)
      // quarter-circle into left edge
      ctx.arcTo(xRight, y + rightSectionHeight, xRight, y + rightSectionHeight - radius, radius)
      // left edge back to top
      ctx.lineTo(xRight, y)
      // finish
      ctx.closePath()
      ctx.fill() // Fill the curve at the bottom-left

      // 3. Position logo at the top-left corner in the header section
      const logoWidthReduction2 = 0.8 // Scale the logo width slightly
      const logoX = 0 // Align logo to the very left edge of the header
      const logoY = headerHeight - logoSize // Vertically align the logo

      // Draw the logo without background
      if (psfLogoImage) {
        ctx.drawImage(psfLogoImage, logoX, logoY, logoSize * logoWidthReduction2, logoSize) // Draw logo image without any background
      }

      // 4. Adjust the exhibition title placement so it aligns well with the logo
      const titleX = logoSize + 90 // Position the title just to the right of the logo
      ctx.fillStyle = "#000000" // Black text color
      ctx.font = "bold 12px stencile" // Font style for the title
      ctx.textAlign = "left" // Align text to the left side
      ctx.fillText(badgeData.badge_info.exhibition_title.toUpperCase(), titleX, 18) // Draw the exhibition title

      // 3. White section - Profile image area (layered over mixed background)
      const whiteAreaHeight = 70 // Reduced to make room for role - no longer fixed
      ctx.fillStyle = mixedGreenBlack
      ctx.fillRect(0, headerHeight, BADGE_WIDTH / 2 + 40, 100) // Covers part of the width, fixed height for photo area

      // Profile image with rounded corners and green shadow border
      const photoWidth = 65 // Made smaller
      const photoHeight = 75 // Made smaller
      const photoX = (BADGE_WIDTH - photoWidth) / 2
      const photoY = headerHeight + 15 // Adjusted position
      // Green shadow border (larger rounded rectangle)
      ctx.save()
      ctx.shadowColor = "rgba(254, 250, 250, 0.1)"
      ctx.shadowBlur = 5
      ctx.shadowOffsetY = 2
      drawRoundedRect(ctx, photoX - 4, photoY - 4, photoWidth + 8, photoHeight + 8, 15)
      ctx.fillStyle = "rgb(254, 250, 250)"
      ctx.fill()
      ctx.restore()

      // Photo area with rounded corners (layered over everything)
      ctx.save()
      drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, 12)
      ctx.fillStyle = "#f8f9fa"
      ctx.fill()
      ctx.clip()
      // Draw photo or placeholder
      if (photoImage) {
        const scale = Math.max(photoWidth / photoImage.width, photoHeight / photoImage.height)
        const scaledWidth = photoImage.width * scale
        const scaledHeight = photoImage.height * scale
        const centerX = photoX + photoWidth / 2
        const centerY = photoY + photoHeight / 2
        const drawX = centerX - scaledWidth / 2
        const drawY = centerY - scaledHeight / 2
        ctx.drawImage(photoImage, drawX, drawY, scaledWidth, scaledHeight)
      } else {
        // Photo placeholder
        ctx.fillStyle = "#6b7280"
        ctx.font = "12px stencile" // Made smaller
        ctx.textAlign = "center"
        ctx.fillText("PHOTO", photoX + photoWidth / 2, photoY + photoHeight / 2 - 8)
        ctx.fillText("PLACEHOLDER", photoX + photoWidth / 2, photoY + photoHeight / 2 + 8)
      }
      ctx.restore()

      // --- NEW: Person Full Name (under profile image) ---
      ctx.fillStyle = "#ffffff" // White text for name
      ctx.textAlign = "center"
      const nameX = photoX + photoWidth / 2
      const nameY = photoY + photoHeight + 27 // 15px padding below photo
      const nameLineHeight = 16
      const nameMaxWidth = photoWidth + 50 // Allow name to be slightly wider than photo
      ctx.font = "bold 14px stencile" // Adjust font size as needed
      const nameHeightUsed = wrapText(
        ctx,
        badgeData.badge_info.person_full_name,
        nameX,
        nameY,
        nameMaxWidth,
        nameLineHeight,
        "center",
      )

      // 4. Role section - positioned under name, with curved top corners (layered over mixed background)
      const roleY = nameY + nameHeightUsed + 10 // Adjusted position based on name
      const roleWidth = BADGE_WIDTH * 0.83 // Made smaller to fit better
      const roleX = (BADGE_WIDTH - roleWidth) / 2 // Center the role box
      const roleHeight = 35 // Made smaller

      // Determine role color - check database color first, fallback to orange
      const displayRole = badgeData.badge_info.role || badgeData.badge_info.stand_category
      let roleColor = PSF_ORANGE // Default fallback
      // Check if role_color exists from database
      if (badgeData.badge_info.role_color) {
        roleColor = badgeData.badge_info.role_color
      }

      // Draw role rectangle - rounded top corners only, limited width (layered over mixed background)
      ctx.save()
      drawRoundedRectTopOnly(ctx, roleX, roleY, roleWidth, roleHeight, 12)
      ctx.fillStyle = roleColor
      ctx.fill()
      ctx.restore()

      // Role text
      ctx.fillStyle = "#ffffff"
      ctx.font = "bold 16px stencile" // Made smaller
      ctx.textAlign = "center"
      // Handle long role text
      let roleText = displayRole
      if (roleText.length > 15) {
        ctx.font = "bold 12px stencile" // Made smaller
        if (roleText.length > 20) {
          roleText = roleText.substring(0, 17) + "..."
        }
      }
      ctx.fillText(roleText, roleX + roleWidth / 2, roleY + 25) // Adjusted position

      // extend role-color down under the green's rounded corner so white never shows
      ctx.save()
      ctx.fillStyle = roleColor
      // this square is exactly the 12×12px quarter‑circle hole in your green
      ctx.fillRect(roleX + roleWidth - 12, roleY + roleHeight, 12, 12)
      ctx.restore()

      // 5. Green section - Limited width, with curved top-right corner matching role (layered over mixed background)
      const adjustedGreenY = roleY + roleHeight
      const greenSectionHeight = BADGE_HEIGHT - adjustedGreenY // Fills remaining height
      const greenWidth = BADGE_WIDTH - 18.5 // Make green section width

      // Draw green section with only top‑right rounded
      ctx.save()
      ctx.fillStyle = PSF_GREEN
      ctx.beginPath()
      // bottom‑left
      ctx.moveTo(0, adjustedGreenY + greenSectionHeight)
      // up left edge
      ctx.lineTo(0, adjustedGreenY)
      // across top, stopping radius before right corner
      ctx.lineTo(greenWidth - 12, adjustedGreenY)
      // arc into right edge
      ctx.arcTo(greenWidth, adjustedGreenY, greenWidth, adjustedGreenY + 12, 12)
      // down right edge
      ctx.lineTo(greenWidth, adjustedGreenY + greenSectionHeight)
      // back along bottom
      ctx.lineTo(0, adjustedGreenY + greenSectionHeight)
      ctx.closePath()
      ctx.fill()
      ctx.restore()

      // --- NEW: Company Name (Exhibitor Name) above QR/Logo ---
      ctx.fillStyle = "#ffffff"
      ctx.textAlign = "center" // Always center company name
      const companyFont = "bold 12px stencile"
      ctx.font = companyFont
      const companyY = adjustedGreenY + 15 // Position company name at the top of the green section
      const companyMaxWidth = BADGE_WIDTH * 0.9 // Max width for company name
      const companyX = BADGE_WIDTH / 2 // Center for company name
      const companyLineHeight = 12
      const companyHeightUsed = wrapText(
        ctx,
        badgeData.badge_info.exhibitor_name,
        companyX,
        companyY,
        companyMaxWidth,
        companyLineHeight,
        "center",
      )

      // Logo and QR Code below company name
      const qrLogoY = companyY + companyHeightUsed + 1 // Position QR/Logo below company name
      const availableHeightForQrLogo = BADGE_HEIGHT - qrLogoY // Remaining height for QR/Logo

      if (showLogo && psfLogoImage) {
        // Version 2: Logo (left) + QR (right) below company name
        const logoSize = 44 // Set both logo and QR to the same height
        const logoWidthReduction = 0.8 // Scale the width to 80% of the original size
        const logoX = 21 // Left aligned
        const logoY = qrLogoY + (availableHeightForQrLogo - logoSize) / 2 // Vertically center in remaining space

        // Draw logo background and image with reduced width
        ctx.fillStyle = "#ffffff"
        ctx.fillRect(logoX - 2, logoY - 2, logoSize * logoWidthReduction + 4, logoSize + 3)
        ctx.drawImage(psfLogoImage, logoX, logoY, logoSize * logoWidthReduction, logoSize) // Reduced width

        const qrSize = 44 // Set the QR code height to match the logo
        const qrX = BADGE_WIDTH - qrSize - 23 // Right aligned
        const qrY = qrLogoY + (availableHeightForQrLogo - qrSize) / 2 // Vertically center in remaining space

        // QR shadow
        ctx.save()
        ctx.shadowColor = PSF_GREEN
        ctx.shadowBlur = 6
        ctx.shadowOffsetX = 2
        ctx.shadowOffsetY = 3
        drawRoundedRect(ctx, qrX - 2, qrY - 2, qrSize + 1, qrSize + 4, 6)
        ctx.fillStyle = PSF_GREEN
        ctx.fill()
        ctx.restore()

        // QR code image with rounded corners (layered)
        ctx.save()
        drawRoundedRect(ctx, qrX, qrY, qrSize, qrSize, 4)
        ctx.clip()
        ctx.drawImage(qrImageElement, qrX, qrY, qrSize, qrSize) // Use qrImageElement directly
        ctx.restore()
      } else {
        // Version 1: QR (centered) below company name
        const qrSize = 44 // Reduced QR size from 60 to 40
        const qrX = (BADGE_WIDTH - qrSize) / 2 // Centered horizontally
        const qrY = qrLogoY + (availableHeightForQrLogo - qrSize) / 2 // Vertically center in remaining space

        // QR shadow
        ctx.save()
        ctx.shadowColor = PSF_GREEN
        ctx.shadowBlur = 6
        ctx.shadowOffsetX = 2
        ctx.shadowOffsetY = 3

        drawRoundedRect(ctx, qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 6)
        ctx.fillStyle = PSF_GREEN
        ctx.fill()
        ctx.restore()

        // QR code image with rounded corners (layered)
        ctx.save()
        drawRoundedRect(ctx, qrX, qrY, qrSize, qrSize, 4)
        ctx.clip()
        ctx.drawImage(qrImageElement, qrX, qrY, qrSize, qrSize) // Use qrImageElement directly
        ctx.restore()
      }

      // Generate final badge image
      const dataURL = canvas.toDataURL("image/png", 1.0)
      setBadgeImage(dataURL)
      onComplete(dataURL)
      setIsGenerationComplete(true)
      setIsRendering(false)
    } catch (error) {
      console.error("Badge generation failed:", error)
      toast.error("Failed to generate badge")
      setIsRendering(false)
    }
  }, [badgeData, photoImage, psfLogoImage, qrImageElement, showLogo])

  // Trigger badge generation when all data and images are ready
  useEffect(() => {
    const allImagesLoaded = photoLoaded && (showLogo ? psfLogoImage !== null : true) && qrImageElement !== null
    if (isOpen && badgeData && allImagesLoaded && !isRendering) {
      setExhibitionText(badgeData.badge_info.exhibition_title)
      generateBadge()
    }
  }, [isOpen, badgeData, photoLoaded, psfLogoImage, qrImageElement, showLogo, generateBadge, isRendering])

  // Update the download function to reflect preview mode
  const downloadBadge = () => {
    if (badgeImage && badgeData) {
      const link = document.createElement("a")
      link.href = badgeImage
      const filename = isPreviewMode
        ? `badge-preview-${badgeData.badge_info.person_full_name.replace(/\s+/g, "-")}.png`
        : `psf-badge-${badgeData.badge_info.person_full_name.replace(/\s+/g, "-")}-${badgeData.badge_info.sequence_number.toString().padStart(4, "0")}.png`
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      const message = isPreviewMode
        ? "Badge preview downloaded successfully!"
        : "PSF Badge downloaded successfully!"
      toast.success(message)
    }
  }

  const printBadge = () => {
    if (badgeImage && badgeData) {
      const printWindow = window.open("", "_blank")
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>PSF Badge Print - ${badgeData.badge_info.person_full_name}</title>
              <style>
                body {
                  margin: 0;
                  padding: 0;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  background: white;
                }
                .badge-container {
                  text-align: center;
                }
                img {
                  width: 400px;
                  height: 600px;
                  display: block;
                }
                @media print {
                  body {
                    margin: 0;
                    padding: 0;
                  }
                  .badge-container {
                    width: 100%;
                    height: 100vh;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  }
                }
              </style>
            </head>
            <body>
              <div class="badge-container">
                <img src="${badgeImage}" alt="PSF Badge" />
              </div>
              <script>
                window.onload = function() {
                  setTimeout(() => {
                    window.print();
                    setTimeout(() => window.close(), 1000);
                  }, 500);
                }
              </script>
            </body>
          </html>
        `)
        printWindow.document.close()
      }
    }
  }

  const copyVerificationCode = () => {
    if (badgeData?.badge_info.verification_code) {
      navigator.clipboard
        .writeText(badgeData.badge_info.verification_code)
        .then(() => {
          setCopied(true)
          toast.success("Verification code copied to clipboard!")
          setTimeout(() => setCopied(false), 3000)
        })
        .catch((err) => {
          console.error("Failed to copy: ", err)
          toast.error("Failed to copy verification code")
        })
    }
  }

  const viewQRCode = () => {
    if (qrCodeImage && badgeData) {
      const qrWindow = window.open("", "_blank")
      if (qrWindow) {
        qrWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>PSF QR Verification - ${badgeData.badge_info.person_full_name}</title>
              <style>
                body {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  margin: 0;
                  font-family: Arial, sans-serif;
                  padding: 20px;
                }
                .qr-container {
                  text-align: center;
                  padding: 40px;
                  background: white;
                  border-radius: 20px;
                  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                  max-width: 500px;
                  width: 100%;
                }
                h1 {
                  color: #ff6b35;
                  margin-bottom: 10px;
                  font-weight: bold;
                  font-size: 28px;
                }
                .subtitle {
                  color: #22c55e;
                  margin-bottom: 30px;
                  font-size: 16px;
                  font-weight: bold;
                }
                .qr-image {
                  border: 3px solid #22c55e;
                  border-radius: 15px;
                  margin: 20px 0;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .info-section {
                  background: linear-gradient(135deg, #fff5f0 0%, #f0fdf4 100%);
                  border-radius: 15px;
                  padding: 25px;
                  margin-top: 25px;
                  text-align: left;
                }
                .verification-highlight {
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  color: white;
                  padding: 15px;
                  border-radius: 10px;
                  margin-top: 20px;
                  font-weight: bold;
                  text-align: center;
                }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <h1>🔒 Crown-style PSF QR Verification</h1>
                <p class="subtitle">Private Sector Federation - Rwanda</p>
                <img src="${qrCodeImage}" alt="Verification QR Code" width="220" height="220" class="qr-image" />
                <div class="info-section">
                  <h3 style="color: #ff6b35;">Badge Information</h3>
                  <p><strong>Name:</strong> ${badgeData.badge_info.person_full_name}</p>
                  <p><strong>Role:</strong> ${badgeData.badge_info.role}</p>
                  <p><strong>Company:</strong> ${badgeData.badge_info.exhibitor_name}</p>
                  <p><strong>Exhibition:</strong> ${badgeData.badge_info.exhibition_title}</p>
                  <p><strong>Stand Category:</strong> ${badgeData.badge_info.stand_category}</p>
                  ${badgeData.badge_info.exhibition_venue ? `<p><strong>Venue:</strong> ${badgeData.badge_info.exhibition_venue}</p>` : ""}
                  <div class="verification-highlight">
                    🔐 Verification Code: ${badgeData.badge_info.verification_code}
                  </div>
                </div>
              </div>
            </body>
          </html>
        `)
        qrWindow.document.close()
      }
    }
  }

  return (
    <>
      {/* Hidden high-resolution canvas for generation */}
      <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />
      <Modal isOpen={isOpen} toggle={onToggle} size="xl" centered>
        
        <ModalHeader
          toggle={onToggle}
          className="text-white border-0"
          style={{ background: "linear-gradient(135deg, #ff6b35 0%, #22c55e 100%)" }}
        >
          <div className="d-flex align-items-center">
            <FaIdCard className="me-3" size={24} />
            <div>
              <h4 className="mb-0">{isPreviewMode ? "🔍 Badge Preview" : "✨PSF Rwanda Badge Designer"}</h4>
              {badgeData && (
                <small className="opacity-75">
                  {badgeData.badge_info.person_full_name} •{" "}
                  {isPreviewMode ? "PREVIEW MODE" : badgeData.badge_info.print_type.toUpperCase()}
                </small>
              )}
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="p-0">
          {badgeData ? (
            <div>
              <Row className="g-0">
                <Col lg={12} className="p-3">
                  <div className="position-relative">
                    <div className="text-center">
                      {badgeImage ? (
                        <img
                          ref={canvasRef}
                          src={badgeImage || "/placeholder.svg"}
                          alt="PSF Badge"
                          className="img-fluid shadow-lg"
                          style={{
                            maxWidth: "400px",
                            maxHeight: "600px", // Portrait orientation
                            borderRadius: "12px",
                            border: "3px solid #ffffff",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex flex-column justify-content-center align-items-center bg-white shadow-lg"
                          style={{
                            width: "400px",
                            height: "600px", // Portrait orientation
                            borderRadius: "12px",
                            border: "3px solid #ff6b35",
                            margin: "0 auto",
                          }}
                        >
                          <Spinner style={{ color: "#ff6b35" }} size="lg" />
                          <p className="mt-3 text-muted">Generating PSF badge...</p>
                        </div>
                      )}
                    </div>
                    {isRendering && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                        style={{
                          background: "rgba(255, 255, 255, 0.95)",
                          borderRadius: "12px",
                          backdropFilter: "blur(5px)",
                        }}
                      >
                        <Spinner style={{ color: "#ff6b35" }} size="lg" />
                        <p className="mt-3 text-dark fw-medium">Rendering PSF badge...</p>
                      </div>
                    )}
                  </div>
                </Col>
                
              </Row>
              {/* Action buttons */}
              <div className="border-top bg-white p-4">
       
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  {!isPreviewMode && (
                    <Button
                      onClick={printBadge}
                      className="d-flex align-items-center px-4 py-2 fw-medium"
                      style={{
                        background: "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)",
                        border: "none",
                      }}
                      disabled={!badgeImage}
                    >
                      <FaPrint className="me-2" />
                      Print Badge
                    </Button>
                  )}
                  <Button
                    onClick={downloadBadge}
                    className="d-flex align-items-center px-4 py-2 fw-medium"
                    style={{
                      background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                      border: "none",
                    }}
                    disabled={!badgeImage}
                  >
                    <FaDownload className="me-2" />
                    {isPreviewMode ? "Download Preview PNG" : "Download Crown PNG"}
                  </Button>
                  {!isPreviewMode && (
                    <Button
                      onClick={viewQRCode}
                      className="d-flex align-items-center px-4 py-2 fw-medium"
                      disabled={!qrCodeImage}
                      style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                        border: "none",
                      }}
                    >
                      <FaQrcode className="me-2" />
                      View QR Details
                    </Button>
                  )}
                  <Button
                    onClick={() => setShouldClose(true)}
                    className="d-flex align-items-center px-4 py-2 fw-medium"
                    style={{
                      background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                      border: "none",
                    }}
                    disabled={!isGenerationComplete}
                  >
                    <FaCheck className="me-2" />
                    {isPreviewMode ? "Close Preview" : "Complete & Close"}
                  </Button>
                </div>
              </div>
              {!badgeImage && !isRendering && (
                <Alert color="warning" className="m-4 text-center">
                  <FaExclamationTriangle className="me-2" />
                  <strong>Preparing crown-style PSF badge generation...</strong>
                  <br />
                  <small>Loading resources and preparing crown design with connected sections</small>
                </Alert>
              )}
              {copied && (
                <Alert color="success" className="m-4 text-center">
                  <FaCheck className="me-2" />
                  <strong>Verification code copied to clipboard!</strong>
                </Alert>
              )}
            </div>
          ) : (
            <div className="p-5 text-center">
              <Alert color="danger" className="border-0 shadow-sm">
                <div className="d-flex flex-column align-items-center">
                  <FaTimes size={48} className="text-danger mb-3" />
                  <h4 className="text-danger fw-bold">No PSF Badge Data Available</h4>
                  <p className="mb-0 text-muted">
                    Please provide valid badge information to generate a crown-style PSF Rwanda badge.
                  </p>
                </div>
              </Alert>
            </div>
          )}
        </ModalBody>
      </Modal>
      <style>{`
        .space-y-4 > * + * {
          margin-top: 1rem;
        }
        .space-y-2 > * + * {
          margin-top: 0.5rem;
        }
      `}</style>
    </>
  )
}

export default EnhancedPSFBadgeDesigner
